import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { retry, timer } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoggingService } from '../services/logging.service';

/**
 * Retry interceptor
 * Automatically retries failed HTTP requests with exponential backoff
 * Only retries on specific error codes (network errors, 500+)
 * 
 * Register in app.config.ts
 */
export const retryInterceptor: HttpInterceptorFn = (req, next) => {
  const logger = inject(LoggingService);

  // Skip retry for specific methods or endpoints
  const skipRetry =
    req.method === 'POST' ||
    req.method === 'PUT' ||
    req.method === 'DELETE' ||
    req.headers.has('X-Skip-Retry');

  if (skipRetry) {
    return next(req);
  }

  const maxRetries = environment.http.retryAttempts;
  const delayMs = environment.http.retryDelay;

  return next(req).pipe(
    retry({
      count: maxRetries,
      delay: (error: HttpErrorResponse, retryCount) => {
        // Only retry on specific errors
        const shouldRetry = shouldRetryRequest(error);

        if (!shouldRetry) {
          throw error;
        }

        // Exponential backoff: delay * 2^retryCount
        const backoffDelay = delayMs * Math.pow(2, retryCount - 1);

        logger.warn(
          `Retrying request (attempt ${retryCount}/${maxRetries})`,
          {
            url: req.url,
            status: error.status,
            delay: backoffDelay,
          }
        );

        return timer(backoffDelay);
      },
    })
  );
};

/**
 * Determine if request should be retried based on error
 */
function shouldRetryRequest(error: HttpErrorResponse): boolean {
  // Network errors (0 status code)
  if (error.status === 0) {
    return true;
  }

  // Server errors (500-599)
  if (error.status >= 500 && error.status < 600) {
    return true;
  }

  // Rate limit (429) - could retry with backoff
  if (error.status === 429) {
    return true;
  }

  // Don't retry for other errors
  return false;
}


